import React, { memo, useCallback, useState } from 'react';
import { ActivityIndicator, FlatList, Pressable } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { PREDEFINED_USER_ROLES, ROLE_PERMISSIONS } from '../../../common/constants';
import { getUserName } from '../../../common/utils/user/getUserName';
import { Text, TextInput } from '../../components';
import { Sheet } from '../../design/components';
import { GET_SIGN_OFF_MANAGERS } from '../../graphql/user';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import usePagedSearchQuery from '../../hooks/usePagedSearchQuery';
import SelectField from '../roster/components/SelectField';

const FIELD_LABEL = 'Manager who inspected the vehicle';

// Only the state's admins (Admin role) sign off a vehicle check; the server
// also requires the role to approve checklists, so both filters apply.
const MANAGER_FILTER = {
	enabled: true,
	excludeFromSelectors: false,
	predefinedRoleName: PREDEFINED_USER_ROLES.ADMIN,
	rolePermission: ROLE_PERMISSIONS.APPROVE_CHECKLIST_SUBMISSIONS,
};

const ManagerOption = memo(({ manager, isSelected, isDisabled, onSelect }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const name = getUserName(manager);

	return (
		<Pressable
			onPress={() => onSelect(manager)}
			disabled={isDisabled}
			accessibilityRole="button"
			accessibilityLabel={isDisabled ? `${name}, you cannot sign off your own vehicle check` : name}
			accessibilityState={{ selected: isSelected, disabled: isDisabled }}
			style={({ pressed }) => [styles.option, isSelected && styles.optionSelected, pressed && styles.pressed]}
		>
			<Text style={[styles.optionLabel, isSelected && styles.optionLabelSelected, isDisabled && styles.optionLabelDisabled]}>
				{isDisabled ? `${name} (you)` : name}
			</Text>
			{isSelected && <Feather name="check" size={20} color={colors.accent} />}
		</Pressable>
	);
});

/**
 * ManagerSignOffSelect: picks the manager who inspected the vehicle from a
 * slide-up list with server search and paging (every eligible manager is
 * findable). The driver can never sign off their own vehicle check.
 * Props: value (user or null), onChange(user), currentUserId
 */
const ManagerSignOffSelect = ({ value, onChange, currentUserId }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [isOpen, setIsOpen] = useState(false);
	const {
		search,
		setSearch,
		query,
		resetSearch,
		items: managers,
		loading,
		error,
		refetch,
		loadMore,
	} = usePagedSearchQuery({
		document: GET_SIGN_OFF_MANAGERS,
		field: 'getUsers',
		filter: MANAGER_FILTER,
		isActive: isOpen,
	});

	const open = useCallback(() => {
		resetSearch();
		setIsOpen(true);
	}, [resetSearch]);
	const close = useCallback(() => setIsOpen(false), []);
	const select = useCallback((manager) => {
		onChange(manager);
		setIsOpen(false);
	}, [onChange]);

	const renderManager = useCallback(({ item }) => (
		<ManagerOption
			manager={item}
			isSelected={item.id === value?.id}
			isDisabled={item.id === currentUserId}
			onSelect={select}
		/>
	), [currentUserId, select, value?.id]);

	let listNote = null;
	if (managers && !managers.length) {
		listNote = query ? 'No manager matches this name.' : 'No managers to select.';
	}
	else if (!managers && error && !loading) {
		listNote = 'Managers could not be loaded. Check your connection and try again.';
	}

	return (
		<>
			<SelectField
				label={FIELD_LABEL}
				showLabel={false}
				valueLabel={value ? getUserName(value) : null}
				placeholder="Select manager"
				onPress={open}
			/>
			<Sheet isVisible={isOpen} onClose={close} title="Select manager" fullHeight={true} scroll={false}>
				<TextInput
					value={search}
					onChange={setSearch}
					placeholder="Search manager name"
					autoCorrect={false}
					autoCapitalize="none"
					accessibilityLabel="Search manager name"
				/>
				<FlatList
					data={managers || []}
					keyExtractor={manager => manager.id}
					renderItem={renderManager}
					extraData={value?.id}
					onEndReached={loadMore}
					onEndReachedThreshold={0.5}
					keyboardShouldPersistTaps="handled"
					style={styles.list}
					contentContainerStyle={styles.listContent}
					ListFooterComponent={(
						<>
							{loading && <ActivityIndicator color={colors.accent} style={styles.loading} />}
							{Boolean(listNote) && <Text style={styles.note}>{listNote}</Text>}
							{!managers && error && !loading && (
								<Pressable onPress={() => refetch()} accessibilityRole="button" style={styles.retry}>
									<Text style={styles.retryText}>Try again</Text>
								</Pressable>
							)}
						</>
					)}
				/>
			</Sheet>
		</>
	);
};

const createStyles = ({ ui: { colors, radius, space, touch, type } }) => ({
	list: {
		flex: 1,
	},
	listContent: {
		gap: space.sm,
	},
	option: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
		minHeight: touch.row,
		paddingHorizontal: 14,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	optionSelected: {
		borderColor: colors.currentBorder,
		backgroundColor: colors.current,
	},
	pressed: {
		opacity: 0.85,
	},
	optionLabel: {
		...type.rowText,
		color: colors.text,
		flex: 1,
	},
	optionLabelSelected: {
		...type.rowTitleSemibold,
	},
	optionLabelDisabled: {
		color: colors.disabledText,
	},
	loading: {
		paddingVertical: space.lg,
	},
	note: {
		...type.secondary,
		color: colors.textSecondary,
		textAlign: 'center',
		paddingVertical: space.sm,
	},
	retry: {
		minHeight: touch.min,
		alignItems: 'center',
		justifyContent: 'center',
	},
	retryText: {
		...type.rowTitle,
		color: colors.accent,
	},
});

export default ManagerSignOffSelect;
